import React, { useEffect, useState } from 'react';
import RoleForm from './RoleForm';
import '../../css/roleList.css';
import apiService from '../../shared/services/ApiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RoleList = () => {
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [sortBy, setSortBy] = useState('name');
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(9);
    
    useEffect(() => {
        fetchRoles();
    }, []);
    
    const fetchRoles = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.get(`/api/roles`);
            setRoles(response.data);
        } catch (error) {
            console.error("Error fetching roles:", error);
            toast.error('Error al cargar los roles');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleDelete = async (id, name) => {
        if (window.confirm(`¿Estás seguro que deseas eliminar el rol "${name}"?`)) {
            try {
                await apiService.delete(`/api/roles/${id}`);
                toast.success(`Rol "${name}" eliminado correctamente`);
                fetchRoles();
            } catch (error) {
                console.error("Error deleting role:", error);
                toast.error('Error al eliminar el rol');
            }
        }
    };
    
    const handleEdit = (role) => {
        setSelectedRole(role);
        setShowForm(true);
        setTimeout(() => {
            const formElement = document.querySelector('.role-form-container-modern');
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };
    
    const handleSubmit = async (roleData) => {
        setIsLoading(true);
        try {
            if (selectedRole) {
                await apiService.put(`/api/roles/${selectedRole._id}`, roleData);
                toast.success('Rol actualizado correctamente');
            } else {
                await apiService.post(`/api/roles`, roleData);
                toast.success('Rol creado correctamente');
            }
            setSelectedRole(null);
            setShowForm(false);
            fetchRoles();
        } catch (error) {
            console.error("Error saving role:", error);
            toast.error(selectedRole ? 'Error al actualizar el rol' : 'Error al crear el rol');
            setIsLoading(false);
        }
    };

    // Filtrado y ordenamiento
    let filteredRoles = roles.filter(role => {
        const matchesSearch = 
            role.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()));
        
        return matchesSearch;
    });

    // Ordenamiento
    filteredRoles = [...filteredRoles].sort((a, b) => {
        if (sortBy === 'name') {
            return a.name.localeCompare(b.name);
        }
        return 0;
    });

    // Estadísticas
    const statistics = {
        total: roles.length,
        withDescription: roles.filter(r => r.description && r.description.trim()).length,
        withoutDescription: roles.filter(r => !r.description || !r.description.trim()).length,
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRoles = filteredRoles.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
    
    return (
        <div className="role-management-container">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="role-header-modern">
                <div className="header-content-role">
                    <div className="header-title-section-role">
                        <div className="title-icon-role">
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                                <path d="M2 17l10 5 10-5"/>
                                <path d="M2 12l10 5 10-5"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="page-title-role">Gestión de Roles</h1>
                            <p className="page-subtitle-role">Administra los roles del sistema</p>
                        </div>
                    </div>
                    <div className="header-actions-role">
                        <button
                            className={`btn-primary-role ${showForm ? 'btn-danger-role' : ''}`}
                            onClick={() => {
                                setShowForm(!showForm);
                                if (showForm) {
                                    setSelectedRole(null);
                                }
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {showForm ? (
                                    <>
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </>
                                ) : (
                                    <>
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <line x1="5" y1="12" x2="19" y2="12"/>
                                    </>
                                )}
                            </svg>
                            {showForm ? 'Cancelar' : 'Nuevo Rol'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Estadísticas */}
            <div className="stats-section-role">
                <div className="stat-card-role total">
                    <div className="stat-icon-role">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                            <path d="M2 17l10 5 10-5"/>
                            <path d="M2 12l10 5 10-5"/>
                        </svg>
                    </div>
                    <div className="stat-content-role">
                        <h3>{statistics.total}</h3>
                        <p>Total Roles</p>
                    </div>
                </div>

                <div className="stat-card-role with-desc">
                    <div className="stat-icon-role">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                        </svg>
                    </div>
                    <div className="stat-content-role">
                        <h3>{statistics.withDescription}</h3>
                        <p>Con Descripción</p>
                    </div>
                </div>

                <div className="stat-card-role without-desc">
                    <div className="stat-icon-role">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                        </svg>
                    </div>
                    <div className="stat-content-role">
                        <h3>{statistics.withoutDescription}</h3>
                        <p>Sin Descripción</p>
                    </div>
                </div>

                <div className="stat-card-role active">
                    <div className="stat-icon-role">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                        </svg>
                    </div>
                    <div className="stat-content-role">
                        <h3>{statistics.total > 0 ? 'Activo' : 'Inactivo'}</h3>
                        <p>Estado del Sistema</p>
                    </div>
                </div>
            </div>

            {/* Formulario */}
            {showForm && (
                <div className="form-wrapper-role">
                    <RoleForm 
                        onSubmit={handleSubmit} 
                        role={selectedRole} 
                        onCancel={() => {
                            setSelectedRole(null);
                            setShowForm(false);
                        }}
                    />
                </div>
            )}

            {/* Lista de roles */}
            <div className="role-list-section-modern">
                {/* Controles */}
                <div className="list-controls-role">
                    <div className="controls-left-role">
                        <div className="search-box-role">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                placeholder="Buscar roles..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {searchTerm && (
                                <button 
                                    className="clear-search-btn-role"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        <select
                            className="filter-select-role"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name">Ordenar por nombre</option>
                        </select>
                    </div>

                    <div className="controls-right-role">
                        <div className="view-toggles-role">
                            <button
                                className={`view-btn-role ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => setViewMode('grid')}
                                title="Vista de cuadrícula"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"/>
                                    <rect x="14" y="3" width="7" height="7"/>
                                    <rect x="14" y="14" width="7" height="7"/>
                                    <rect x="3" y="14" width="7" height="7"/>
                                </svg>
                            </button>
                            <button
                                className={`view-btn-role ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                                title="Vista de lista"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="8" y1="6" x2="21" y2="6"/>
                                    <line x1="8" y1="12" x2="21" y2="12"/>
                                    <line x1="8" y1="18" x2="21" y2="18"/>
                                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Contenido */}
                {isLoading ? (
                    <div className="loading-state-role">
                        <div className="spinner-role"></div>
                        <p>Cargando roles...</p>
                    </div>
                ) : (
                    <>
                        {currentRoles.length > 0 ? (
                            <div className={`roles-${viewMode}`}>
                                {currentRoles.map((role) => (
                                    <div key={role._id} className="role-card-modern">
                                        <div className="role-card-header">
                                            <div className="role-avatar-modern">
                                                {role.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="role-info-card">
                                                <h3 className="role-name-card">{role.name}</h3>
                                                {role.description ? (
                                                    <p className="role-description-card">{role.description}</p>
                                                ) : (
                                                    <p className="role-no-description">Sin descripción</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="role-card-footer">
                                            <div className="role-badge">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                                </svg>
                                                {role.permissions?.length || 0} permisos
                                            </div>
                                            <div className="role-card-actions">
                                                <button
                                                    className="action-btn-role edit"
                                                    onClick={() => handleEdit(role)}
                                                    title="Editar rol"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                    </svg>
                                                </button>
                                                <button
                                                    className="action-btn-role delete"
                                                    onClick={() => handleDelete(role._id, role.name)}
                                                    title="Eliminar rol"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"/>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                        <line x1="10" y1="11" x2="10" y2="17"/>
                                                        <line x1="14" y1="11" x2="14" y2="17"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state-role">
                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                                    <path d="M2 17l10 5 10-5"/>
                                    <path d="M2 12l10 5 10-5"/>
                                </svg>
                                <h3>No hay roles</h3>
                                <p>
                                    {searchTerm
                                        ? `No se encontraron roles que coincidan con "${searchTerm}"`
                                        : 'No hay roles registrados. Crea uno nuevo para comenzar.'}
                                </p>
                                {searchTerm && (
                                    <button
                                        className="btn-clear-filters-role"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        Limpiar búsqueda
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Paginación */}
                        {totalPages > 1 && (
                            <div className="pagination-role">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="page-btn-role"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15 18 9 12 15 6"/>
                                    </svg>
                                </button>

                                <div className="page-numbers-role">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(num =>
                                            num === 1 ||
                                            num === totalPages ||
                                            (num >= currentPage - 1 && num <= currentPage + 1)
                                        )
                                        .map((number, index, array) => {
                                            if (index > 0 && array[index - 1] !== number - 1) {
                                                return (
                                                    <React.Fragment key={`ellipsis-${number}`}>
                                                        <span className="ellipsis-role">...</span>
                                                        <button
                                                            onClick={() => setCurrentPage(number)}
                                                            className={`page-btn-role ${currentPage === number ? 'active' : ''}`}
                                                        >
                                                            {number}
                                                        </button>
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <button
                                                    key={number}
                                                    onClick={() => setCurrentPage(number)}
                                                    className={`page-btn-role ${currentPage === number ? 'active' : ''}`}
                                                >
                                                    {number}
                                                </button>
                                            );
                                        })
                                    }
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="page-btn-role"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6"/>
                                    </svg>
                                </button>
                            </div>
                        )}

                        <div className="pagination-info-role">
                            Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredRoles.length)} de {filteredRoles.length} rol{filteredRoles.length !== 1 ? 'es' : ''}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RoleList;